import { DataGridPremium, type GridColDef, useGridApiRef } from '@mui/x-data-grid-premium'
import type React from 'react'
import { useMemo, useState } from 'react'
import type { VoucherDto } from '../api/swagger/definitions/backoffice'
import ColorsDeprecated from '../figma/tokens/ColorsDeprecated'
import Spacings from '../figma/tokens/Spacings'
import useLanguage from '../flamingo/hooks/useLanguage'
import { TableHeader, useTableGridProps } from '../helpers/getTableOptions'
import { TabSets, VoucherTabs } from '../helpers/TabsHelpers'
import { useAccounting } from '../hooks/useAccounting'
import TextKeys from '../libs/TextKeys'
import FigmaBox from '../mynt-components/components/FigmaBox'
import Tabs from '../mynt-components/components/Tabs'
import { getText } from '../tiger/libs/TextRepository'
import TableWrapper from './TableWrapper'
import TextContainer from './TextContainer'
import VouchersFilters from './VouchersFilters'
import VouchersSummaryTable from './VouchersSummaryTable'

const VouchersTable: React.FC = () => {
  const language = useLanguage()

  const { years = [], vouchers = [], summary, vouchersLoading, summaryLoading, accountingFilters } = useAccounting()

  const [activeTab, setActiveTab] = useState(0)

  const gridProps = useTableGridProps({ headerTextKey: TextKeys.accountingPaymentsOCR })

  const apiRef = useGridApiRef()

  const columns = useMemo<GridColDef<VoucherDto>[]>(
    () => [
      {
        headerName: getText(TextKeys.voucherListAccountingYearLabel, language),
        field: 'accountingYearId',
        flex: 1,
        valueGetter: (_, row) => years?.find((year) => year.id === row.accountingYearId)?.name ?? '-',
      },
      {
        headerName: getText(TextKeys.voucherListAccountingDateLabel, language),
        field: 'accountingDate',
        flex: 1,
      },
      {
        headerName: getText(TextKeys.voucherListDescriptionLabel, language),
        field: 'description',
        flex: 1,
      },
    ],
    [language, years],
  )

  const currencies = useMemo(() => {
    if (!summary) return []

    const currencySet = summary.reduce((acc, next) => {
      next.periods?.forEach((period) => {
        acc.add(period.currency)
      })

      return acc
    }, new Set<string>())

    return Array.from(currencySet)
  }, [summary])

  const tabs = [
    {
      id: 'list',
      labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={VoucherTabs.LIST} />,
      contentElement: (
        <TableWrapper>
          <TableHeader title={getText(TextKeys.vouchersHeading, language)} apiRef={apiRef} />
          <DataGridPremium
            {...gridProps}
            apiRef={apiRef}
            rows={vouchers}
            columns={columns}
            loading={vouchersLoading}
            initialState={{
              ...gridProps.initialState,
              sorting: { sortModel: [{ field: 'accountingDate', sort: 'desc' }] },
            }}
            sx={{
              ...gridProps.sx,
              '& .MuiDataGrid-columnHeader': { backgroundColor: ColorsDeprecated.surfaceOnWhite },
            }}
          />
        </TableWrapper>
      ),
      enable: true,
    },
    {
      id: 'summary',
      labelElement: <TextContainer textKey={TextKeys.tabNotSelected} text={VoucherTabs.SUMMARY} />,
      contentElement: <VouchersSummaryTable currencies={currencies} summary={summary || []} loading={summaryLoading} />,
      enable: true,
    },
  ]

  const handleTabChange = (activeIndex: number) => {
    setActiveTab(activeIndex)
  }

  return (
    <>
      <FigmaBox fullWidth>
        <FigmaBox fullWidth direction="row" justify="space-between" align="flex-start" gap={Spacings.medium} bottom>
          <VouchersFilters currencies={currencies} years={years} accountingFilters={accountingFilters} />
        </FigmaBox>
        <Tabs
          fullPage
          alwaysShowNavHeadings
          onTabChange={handleTabChange}
          tab={activeTab}
          tabKeyNameSpace={TabSets.ACCOUNTING_VOUCHERS}
          tabs={tabs}
        />
      </FigmaBox>
    </>
  )
}

export default VouchersTable
